import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  Award,
  ArchiveBook,
  Category,
  Clock,
  Lock,
  Play,
  TickCircle,
} from "iconsax-react";
import { lessonsService, Module, Lesson } from "../../services/lessonsService";
import { useSinalario } from "../../hooks/sinalario";
import MobileLayout from "../../components/MobileLayout";
import { fontSizes, lineHeights } from "../../shared/styles/typography";

const ModuleSection = styled.section`
  margin-bottom: 3rem;
`;

const ModuleCard = styled.div<{ color?: string }>`
  background: white;
  border-radius: 1.5rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 2px solid ${(props) => props.color || "#5f33e1"};
  display: flex;
  align-items: center;
  gap: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

  .icon-wrapper {
    width: 64px;
    height: 64px;
    border-radius: 1rem;
    background: ${(props) => props.color || "#5f33e1"}20;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${(props) => props.color || "#5f33e1"};
  }

  .content {
    flex: 1;
    h2 {
      margin: 0;
      font-size: ${fontSizes.xl}px;
      line-height: ${lineHeights.xl}px;
      font-weight: 700;
      color: #1a1a1a;
    }
    p {
      margin: 0.25rem 0 0;
      color: #666;
      font-size: ${fontSizes.sm}px;
      line-height: ${lineHeights.sm}px;
    }
  }

  .progress-info {
    text-align: right;
    span {
      display: block;
      font-weight: var(--font-weight-semibold);
      color: ${(props) => props.color || "#5f33e1"};
      font-size: ${fontSizes.base}px;
      line-height: ${lineHeights.base}px;
    }
    label {
      font-size: ${fontSizes.xs}px;
      line-height: ${lineHeights.xs}px;
      color: #999;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
  }
`;

const LessonsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.25rem;
`;

const LessonCard = styled.button<{ $locked?: boolean; $completed?: boolean }>`
  background: white;
  border: 2px solid
    ${(props) => {
      if (props.$completed) return "#10b981";
      if (props.$locked) return "#e5e7eb";
      return "#f3f4f6";
    }};
  border-radius: 1.25rem;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: left;
  transition: all 0.2s ease;
  cursor: ${(props) => (props.$locked ? "not-allowed" : "pointer")};
  position: relative;
  overflow: hidden;

  &:hover {
    ${(props) =>
      !props.$locked &&
      `
      border-color: #5f33e1;
      transform: translateY(-4px);
      box-shadow: 0 8px 24px rgba(95, 51, 225, 0.1);
    `}
  }

  .lesson-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .index {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: ${(props) => {
        if (props.$completed) return "#10b981";
        if (props.$locked) return "#e5e7eb";
        return "#5f33e1";
      }};
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: var(--font-weight-semibold);
      font-size: ${fontSizes.sm}px;
      line-height: ${lineHeights.sm}px;
    }

    .status-icon {
      color: ${(props) => {
        if (props.$completed) return "#10b981";
        if (props.$locked) return "#999";
        return "#5f33e1";
      }};
    }
  }

  .lesson-body {
    h3 {
      margin: 0;
      font-size: ${fontSizes.base}px;
      line-height: ${lineHeights.base}px;
      font-weight: 600;
      color: ${(props) => (props.$locked ? "#999" : "#1a1a1a")};
    }
    p {
      margin: 0.5rem 0 0;
      font-size: ${fontSizes.sm}px;
      line-height: ${lineHeights.sm}px;
      color: #777;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }

  .lesson-footer {
    margin-top: auto;
    display: flex;
    align-items: center;
    gap: 1rem;
    font-size: ${fontSizes.sm}px;
    line-height: ${lineHeights.sm}px;
    color: #999;

    .meta-item {
      display: flex;
      align-items: center;
      gap: 0.35rem;
    }
  }

  ${(props) =>
    props.$locked &&
    `
    background: #fafafa;
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.1);
    }
  `}
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 1.5rem;
  color: #666;

  .spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #5f33e1;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: white;
  border-radius: 2rem;
  border: 2px dashed #e5e7eb;

  h2 {
    margin: 1.5rem 0 0.5rem;
    color: #1a1a1a;
  }
  p {
    color: #666;
    margin-bottom: 2rem;
  }
`;

const Lessons: React.FC = () => {
  const navigate = useNavigate();
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);

  const { getVerbete } = useSinalario();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const verbetes = await getVerbete();
      const modulesData = await lessonsService.getModules(verbetes);
      setModules(modulesData);
    } catch (error) {
      console.error("Error loading lessons:", error);
    } finally {
      setLoading(false);
    }
  }, [getVerbete]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLessonPress = (lesson: Lesson) => {
    if (lesson.isLocked) return;
    navigate(`/lessons/${lesson.id}`, { state: { lesson } });
  };

  if (loading) {
    return (
      <MobileLayout headerTitle="Lições" headerType="private">
        <LoadingContainer>
          <div className="spinner" />
          <p>Preparando seus exercícios...</p>
        </LoadingContainer>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout headerTitle="Aprendizado" headerType="private">
      <div style={{ padding: "0 16px", marginTop: "16px" }}>
        {modules.length === 0 ? (
          <EmptyState>
            <ArchiveBook size={64} color="#e5e7eb" variant="Broken" />
            <h2>Nenhuma lição disponível</h2>
            <p>
              Ainda estamos preparando os módulos de ensino para você. Volte em
              breve!
            </p>
          </EmptyState>
        ) : (
          modules.map((module) => (
            <ModuleSection key={module.id}>
              <ModuleCard color={module.color || "#4614BB"}>
                <div className="icon-wrapper">
                  <Category size={32} variant="Bulk" />
                </div>
                <div className="content">
                  <h2>{module.title}</h2>
                  <p>{module.description}</p>
                </div>
                <div className="progress-info">
                  <span>
                    {module.lessons.filter((l) => l.isCompleted).length} /{" "}
                    {module.lessons.length}
                  </span>
                  <span
                    className="label"
                    style={{
                      color: "#94a3b8",
                      fontSize: fontSizes.xs,
                      textTransform: "uppercase",
                    }}
                  >
                    Completas
                  </span>
                </div>
              </ModuleCard>

              <LessonsGrid>
                {module.lessons.map((lesson, index) => (
                  <LessonCard
                    key={lesson.id}
                    $locked={lesson.isLocked}
                    $completed={lesson.isCompleted}
                    onClick={() => handleLessonPress(lesson)}
                  >
                    <div className="lesson-header">
                      <div className="index">{index + 1}</div>
                      <div className="status-icon">
                        {(() => {
                          if (lesson.isCompleted)
                            return <TickCircle size={24} variant="Bold" />;
                          if (lesson.isLocked)
                            return <Lock size={20} variant="Bold" />;
                          return <Play size={24} variant="Bold" />;
                        })()}
                      </div>
                    </div>

                    <div className="lesson-body">
                      <h3>{lesson.title}</h3>
                      <p>{lesson.description}</p>
                    </div>

                    <div className="lesson-footer">
                      <div className="meta-item">
                        <Clock size={16} />
                        <span>{lesson.estimatedTime} min</span>
                      </div>
                      <div className="meta-item">
                        <Award size={16} />
                        <span>{lesson.exercises.length} ex.</span>
                      </div>
                    </div>
                  </LessonCard>
                ))}
              </LessonsGrid>
            </ModuleSection>
          ))
        )}
      </div>
    </MobileLayout>
  );
};

export default Lessons;
